import { BaseSqliteRepository } from './base-sqlite.repository';
import type { IAccountTransferRepository } from '@app/domain/ports';
import type { AccountTransfer } from '@app/domain/entities';
import type { TransferFilter, TransferCursor } from '@app/domain/ports';
import { SqliteQueryError } from '../../errors';

interface TransferRow {
  id: number;
  date: number; // Unix timestamp in seconds
  accountOutId: number;
  accountOutName: string;
  accountInId: number;
  accountInName: string;
  amount: number;
  currencyCode: string;
  note: string | null;
  sourceExpenseId: number | null;
  sourceIncomeId: number | null;
}

export class AccountTransferRepository extends BaseSqliteRepository implements IAccountTransferRepository {
  private readonly BASE_FROM = `
    FROM account_transfers AS t
    INNER JOIN accounts AS aOut ON t.account_out_id = aOut._id
    INNER JOIN accounts AS aIn ON t.account_in_id = aIn._id
    INNER JOIN currencies AS c ON t.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE t.is_deleted = 0
  `;

  private readonly BASE_QUERY = `
    SELECT
      t._id AS id,
      t.account_out_id AS accountOutId,
      aOut.name AS accountOutName,
      t.account_in_id AS accountInId,
      aIn.name AS accountInName,
      cl.ticker AS currencyCode,
      t.transfer_date AS date,
      t.amount AS amount,
      t.note AS note,
      t.source_expense_id AS sourceExpenseId,
      t.source_income_id AS sourceIncomeId
    ${this.BASE_FROM}
  `;

  private readonly COUNT_QUERY = `
    SELECT COUNT(*) AS total
    ${this.BASE_FROM}
  `;

  async count(filter?: TransferFilter): Promise<number> {
    const { query, params } = this.applyFilter(this.COUNT_QUERY, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: TransferCursor, filter?: TransferFilter): Promise<AccountTransfer[]> {
    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(this.BASE_QUERY, filter);

    query += ` ORDER BY t.transfer_date DESC, t._id DESC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<TransferRow>(query, params);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<AccountTransfer | null> {
    const query = this.BASE_QUERY + ` AND t._id = ?`;
    const row = await this.queryOne<TransferRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(transfer: {
    accountOutId: number;
    accountInId: number;
    currencyCode: string;
    date: Date;
    amount: number;
    note: string | null;
    sourceExpenseId?: number | null;
    sourceIncomeId?: number | null;
  }): Promise<number> {
    const unixDate = new Date(transfer.date).setUTCHours(0, 0, 0, 0) / 1000;
    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO account_transfers (account_out_id, account_in_id, currency_id, transfer_date, amount, note, source_expense_id, source_income_id)
       VALUES (?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, ?, ?, ?)
       RETURNING _id AS id`,
      [
        transfer.accountOutId,
        transfer.accountInId,
        transfer.currencyCode,
        unixDate,
        transfer.amount,
        transfer.note,
        transfer.sourceExpenseId ?? null,
        transfer.sourceIncomeId ?? null,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO account_transfers', [], 'Failed to insert account transfer');
    }

    return result.id;
  }

  async update(transfer: {
    id: number;
    accountOutId: number;
    accountInId: number;
    currencyCode: string;
    date: Date;
    amount: number;
    note: string | null;
  }): Promise<void> {
    const unixDate = new Date(transfer.date).setUTCHours(0, 0, 0, 0) / 1000;
    await this.execute(
      `UPDATE account_transfers SET
        account_out_id = ?,
        account_in_id = ?,
        currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
        transfer_date = ?,
        amount = ?,
        note = ?,
        updated_at = strftime('%s','now')
       WHERE _id = ? AND is_deleted = 0`,
      [
        transfer.accountOutId,
        transfer.accountInId,
        transfer.currencyCode,
        unixDate,
        transfer.amount,
        transfer.note ?? null,
        transfer.id,
      ],
    );
  }

  async delete(id: number): Promise<void> {
    // Soft delete
    await this.execute("UPDATE account_transfers SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [
      id,
    ]);
  }

  async aggregateTotalAmount(filter?: TransferFilter): Promise<number> {
    const sql = `
      SELECT SUM(t.amount) AS total
      ${this.BASE_FROM}
    `;
    const { query, params } = this.applyFilter(sql, filter);

    const result = await this.queryOne<{ total: number | null }>(query, params);
    return result?.total ?? 0;
  }

  async findActiveBySourceExpenseId(expenseId: number): Promise<number | null> {
    const row = await this.queryOne<{ id: number }>(
      `SELECT _id AS id
       FROM account_transfers
       WHERE source_expense_id = ? AND is_deleted = 0
       LIMIT 1`,
      [expenseId],
    );
    return row?.id ?? null;
  }

  async findActiveBySourceIncomeId(incomeId: number): Promise<number | null> {
    const row = await this.queryOne<{ id: number }>(
      `SELECT _id AS id
       FROM account_transfers
       WHERE source_income_id = ? AND is_deleted = 0
       LIMIT 1`,
      [incomeId],
    );
    return row?.id ?? null;
  }

  async findSimilarTransfer(params: {
    date: Date;
    accountOutId: number;
    accountInId: number;
    amount: number;
    currencyCode: string;
    amountTolerance: number;
    externalSystem: string;
  }): Promise<number | null> {
    const normalizedDate = new Date(params.date).setUTCHours(0, 0, 0, 0) / 1000;
    const minAmount = params.amount - params.amountTolerance;
    const maxAmount = params.amount + params.amountTolerance;

    const query = `
      SELECT t._id as id
      FROM account_transfers t
      INNER JOIN accounts aOut ON t.account_out_id = aOut._id
      INNER JOIN accounts aIn ON t.account_in_id = aIn._id
      INNER JOIN currencies c ON t.currency_id = c._id
      INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
      WHERE t.is_deleted = 0
        AND t.account_out_id = ?
        AND t.account_in_id = ?
        AND cl.ticker = ?
        AND t.transfer_date = ?
        AND t.amount >= ?
        AND t.amount <= ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions et
          WHERE et.account_transfer_id = t._id
            AND et.source = ?
            AND et.is_deleted = 0
        )
      ORDER BY ABS(t.amount - ?) ASC
      LIMIT 1
    `;

    const row = await this.queryOne<{ id: number }>(query, [
      params.accountOutId,
      params.accountInId,
      params.currencyCode,
      normalizedDate,
      minAmount,
      maxAmount,
      params.externalSystem,
      params.amount,
    ]);

    return row?.id ?? null;
  }

  async countByAccount(accountId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM account_transfers t
      WHERE (t.account_out_id = ? OR t.account_in_id = ?) AND t.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [accountId, accountId]);
    return result?.total ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE account_transfers
       SET account_out_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_out_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
    await this.execute(
      `UPDATE account_transfers
       SET account_in_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_in_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  private applyFilter(
    query: string,
    filter?: TransferFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountOutId) {
      query += ` AND t.account_out_id = ?`;
      params.push(filter.accountOutId);
    }

    if (filter?.accountInId) {
      query += ` AND t.account_in_id = ?`;
      params.push(filter.accountInId);
    }

    if (filter?.currencyCode) {
      query += ` AND cl.ticker = ?`;
      params.push(filter.currencyCode);
    }

    if (filter?.dateFrom) {
      query += ` AND t.transfer_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND t.transfer_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    // Filter out hidden accounts unless showHidden is true
    if (filter?.showHiddenAccounts !== true) {
      query += ` AND aOut.is_hidden = 0 AND aIn.is_hidden = 0`;
    }

    return { query, params };
  }

  private mapRowToEntity(row: TransferRow): AccountTransfer {
    return {
      id: row.id,
      date: new Date(row.date * 1000), // Convert Unix timestamp to Date
      amount: row.amount,
      currencyCode: row.currencyCode,
      accountOutId: row.accountOutId,
      accountOutName: row.accountOutName,
      accountInId: row.accountInId,
      accountInName: row.accountInName,
      note: row.note,
      sourceExpenseId: row.sourceExpenseId ?? null,
      sourceIncomeId: row.sourceIncomeId ?? null,
    };
  }
}
